import React, { useEffect, useRef } from "react";

import { Message } from "./Message/Message";
import styles from "../chatPage.scss";
import { Message as MessageType, UserI, messageType, groupType } from "../../../typings/interfaces";
import { MESSAGES_LIST_DIV_ID } from "../constants/constants";
import { useSelector } from "react-redux";
import { selectLastReadMessageFromOtherUserIndex, selectOtherUsersActiveAdsCount, selectOtherUsersRegistrationDate, selectOtherUsersReplyStatisticsMessage } from "../../../routes/Chat/chat.ducks";
import { PageRoute } from "../../../models";
import { Link } from "react-router-dom";
import { formatDate } from "../../../services";
import { FullDate } from "./FullDate";
import { WarningOutlined } from "@ant-design/icons";
import { InitialContactInfoMessage } from "./InitialContactInfoMessage";

type Props = {
  messages: MessageType[];
  user: UserI;
  isMobile: boolean;
  initialFirstUnreadIndex: number;
  otherChatParticipantInfo: {
    userId?: string;
    name?: string;
    lastReadMessageId?: string;
  };
  senderContactInfo: {
    phoneNumber: string;
    email: string;
    messageId: string;
  } | null;

}

export const MessagesList = ({ messages, user, otherChatParticipantInfo, isMobile, initialFirstUnreadIndex, senderContactInfo }: Props) => {
  const lastReadMessageFromOtherUserIndex = useSelector(selectLastReadMessageFromOtherUserIndex);
  const otherUserRegistrationDate = useSelector(selectOtherUsersRegistrationDate);
  const otherUserActiveAds = useSelector(selectOtherUsersActiveAdsCount);
  const otherUserReplyStatisticsMessage = useSelector(selectOtherUsersReplyStatisticsMessage);

  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    const messagesListDiv = document.getElementById(MESSAGES_LIST_DIV_ID);

    if (isInitialLoadRef.current && messages.length > 0) {
      isInitialLoadRef.current = false;

      // Delay scroll to ensure DOM layout is complete
      setTimeout(() => {
        const doScroll = () => {
          if (initialFirstUnreadIndex >= 0) {
            // Has unread messages - scroll to first unread
            const elements: HTMLElement[] = Array.from(messagesListDiv.querySelectorAll('[data-message-index]'));
            let targetElement: HTMLElement | null = null;
            for (const el of elements) {
              const idx = parseInt(el.getAttribute('data-message-index'), 10);
              if (idx >= initialFirstUnreadIndex) {
                targetElement = el;
                break;
              }
            }
            if (targetElement) {
              messagesListDiv.scrollTop = targetElement.offsetTop - messagesListDiv.offsetTop;
            } else {
              messagesListDiv.scrollTop = messagesListDiv.scrollHeight;
            }
          } else {
            // All read or no unread messages - scroll to bottom
            messagesListDiv.scrollTop = messagesListDiv.scrollHeight;
          }

        };

        doScroll();

        // Re-scroll when images finish loading (they change layout)
        const images = messagesListDiv.getElementsByTagName('img');
        for (let i = 0; i < images.length; i++) {
          if (!images[i].complete) {
            images[i].addEventListener('load', doScroll, { once: true });
          }
        }
      }, 0);
    } else if (!isInitialLoadRef.current) {
      // Subsequent updates - scroll to bottom
      messagesListDiv.scrollTop = messagesListDiv.scrollHeight;
    }
  }, [messages.length])

  // Group consecutive images from the same sender
  const groupMessages = (messages) => {
    const grouped = [];
    let i = 0;

    while (i < messages.length) {
      const currentMessage = messages[i];

      // Check if this is an image message
      if (currentMessage.type === messageType.Image) {
        const imageGroup = [currentMessage];
        let j = i + 1;

        // Collect consecutive image messages from the same sender
        while (
          j < messages.length &&
          messages[j].type === messageType.Image &&
          messages[j].senderId === currentMessage.senderId
        ) {
          imageGroup.push(messages[j]);
          j++;
        }

        // Add the image group
        grouped.push({
          type: groupType.ImageGroup,
          messages: imageGroup,
          senderId: currentMessage.senderId,
          messageId: `group-${currentMessage.messageId}`,
          sentAt: currentMessage.sentAt
        });

        i = j;
      } else {
        // Add single non-image message
        grouped.push({
          type: groupType.Single,
          message: currentMessage,
          messageId: currentMessage.messageId
        });
        i++;
      }
    }

    return grouped;
  };

  const groupedMessages = groupMessages(messages);

  // Compute flat message index for each group
  const groupStartIndices: number[] = [];
  let flatIdx = 0;
  for (const group of groupedMessages) {
    groupStartIndices.push(flatIdx);
    flatIdx += group.type === groupType.ImageGroup ? group.messages.length : 1;
  }

  const shouldShowOtherUserWarning = (() => {
    if (!otherUserRegistrationDate || otherUserActiveAds > 0) {
      return false;
    }
    const registrationDate = new Date(otherUserRegistrationDate);
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return registrationDate > oneWeekAgo;
  })();

  return (
    <div className={styles.messagesList} id={MESSAGES_LIST_DIV_ID}>
      <div style={{ height: messages.length > 0 ? "auto" : "100%" }} className={styles.otherChatParticipantInfoWrapper}>
        <p className={styles.otherChatParticipantInfo}>
          <Link
            to={`${PageRoute.user}/${encodeURIComponent(otherChatParticipantInfo.name)}`}
          >
            {otherChatParticipantInfo && otherChatParticipantInfo.name}
          </Link>
        </p>
        <div style={{ textAlign: "center", fontSize: 11, color: "#828282", marginBottom: 20 }}>
          {`Registriran od ${formatDate(otherUserRegistrationDate)}`}
          {otherUserReplyStatisticsMessage && <React.Fragment><br />{otherUserReplyStatisticsMessage}</React.Fragment>}
          {shouldShowOtherUserWarning && (
            <React.Fragment>
              <br />
              <div className={styles.newUserWarning}>
                <div className={styles.warningContent}>
                  <WarningOutlined className={styles.warningIcon} />
                  <span className={styles.warningTitle}>Novi korisnik, bez oglasa</span>
                </div>
              </div>
            </React.Fragment>
          )}
        </div>



      </div>

      {groupedMessages.map((group, index) => {
        const previousGroup = groupedMessages[index - 1];
        const previousMessage = previousGroup ?
          (previousGroup.type === groupType.Single ? previousGroup.message : previousGroup.messages[previousGroup.messages.length - 1]) :
          null;

        const currentMessage = group.type === groupType.Single ? group.message : group.messages[0];
        const messageIndex = groupStartIndices[index];

        return (
          <div key={group.messageId} data-message-index={messageIndex}>
            <FullDate message={currentMessage} previousMessage={previousMessage} />
            <Message
              message={group.type === groupType.Single ? group.message : null}
              messages={group.type === groupType.ImageGroup ? group.messages : null}
              groupType={group.type}
              user={user}
              isReadByOtherUser={
                group.type === groupType.Single
                  ? messages.findIndex(m => m.messageId === group.message.messageId) <= lastReadMessageFromOtherUserIndex
                  : messages.findIndex(m => m.messageId === group.messages[group.messages.length - 1].messageId) <= lastReadMessageFromOtherUserIndex
              }
            />
            {senderContactInfo && senderContactInfo.messageId === group.messageId && (
              <Message
                message={{
                  ...group.message,
                  content: (
                    <InitialContactInfoMessage
                      senderContactInfo={senderContactInfo}
                      isOwnMessage={group.message.senderId === user.userId}
                      isMobile={isMobile}
                    />)
                }}
                groupType={group.type}
                user={user}
                isReadByOtherUser={
                  group.type === groupType.Single
                    ? messages.findIndex(m => m.messageId === group.message.messageId) <= lastReadMessageFromOtherUserIndex
                    : messages.findIndex(m => m.messageId === group.messages[group.messages.length - 1].messageId) <= lastReadMessageFromOtherUserIndex
                }
              />
            )}

          </div>
        );
      })}
    </div>
  )
}


// WEBPACK FOOTER //
// ./src/containers/ChatsPage/SingleChat/MessagesList.tsx